/**
 * Sanity Check Service
 * Scans the entire game for broken references and provides auto-fix functionality
 */

import { findBrokenReferences, type SanityCheckResult, type BrokenReference } from './ReferenceTrackingService';
import { fixBrokenReferences, type CleanupResult } from './ReferenceCleanupService';

export interface SanityCheckReport extends SanityCheckResult {
  timestamp: string;
  gameId: string;
}

export interface SanityCheckProgress {
  stage: 'scanning' | 'analyzing' | 'complete';
  progress: number; // 0-100
  message: string;
}

/**
 * Run a full sanity check on a game
 */
export async function runSanityCheck(
  gameId: string,
  onProgress?: (progress: SanityCheckProgress) => void
): Promise<SanityCheckReport> {
  try {
    // Stage 1: Scanning
    if (onProgress) {
      onProgress({
        stage: 'scanning',
        progress: 25,
        message: 'Scanning templates and objects...',
      });
    }

    // Run the check
    const result = findBrokenReferences(gameId);

    // Stage 2: Analyzing
    if (onProgress) {
      onProgress({
        stage: 'analyzing',
        progress: 75,
        message: 'Analyzing references...',
      });
    }

    // Stage 3: Complete
    if (onProgress) {
      onProgress({
        stage: 'complete',
        progress: 100,
        message: result.hasBrokenReferences 
          ? `Found ${result.totalCount} broken reference${result.totalCount !== 1 ? 's' : ''}`
          : 'No broken references found',
      });
    }

    return {
      ...result,
      timestamp: new Date().toISOString(),
      gameId,
    };
  } catch (error) {
    console.error('Error running sanity check:', error);
    throw error;
  }
}

/**
 * Auto-fix all broken references found in a sanity check
 */
export async function autoFixBrokenReferences(
  report: SanityCheckReport,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void,
  onProgress?: (progress: { current: number; total: number; message: string }) => void
): Promise<CleanupResult> {
  const { brokenReferences, gameId } = report;
  
  if (brokenReferences.length === 0) {
    return {
      success: true,
      cleanedCount: 0,
      errors: [],
    };
  }

  try {
    if (onProgress) {
      onProgress({
        current: 0,
        total: brokenReferences.length,
        message: 'Starting auto-fix...',
      });
    }

    const result = await fixBrokenReferences(brokenReferences, gameId, toast);

    if (onProgress) {
      onProgress({
        current: brokenReferences.length,
        total: brokenReferences.length,
        message: result.success 
          ? `Fixed ${result.cleanedCount} broken reference${result.cleanedCount !== 1 ? 's' : ''}`
          : 'Some fixes failed',
      });
    }

    return result;
  } catch (error) {
    console.error('Error during auto-fix:', error);
    return {
      success: false,
      cleanedCount: 0,
      errors: [error instanceof Error ? error.message : 'Unknown error'],
    };
  }
}

/**
 * Get a summary of broken references by type
 */
export function getSanityCheckSummary(report: SanityCheckReport): string {
  if (!report.hasBrokenReferences) {
    return 'No issues found. All references are valid.';
  }

  const parts: string[] = [];
  const { byType } = report;

  if (byType.types > 0) {
    parts.push(`${byType.types} broken type reference${byType.types !== 1 ? 's' : ''}`);
  }
  if (byType.strings > 0) {
    parts.push(`${byType.strings} broken string reference${byType.strings !== 1 ? 's' : ''}`);
  }
  if (byType.objects > 0) {
    parts.push(`${byType.objects} broken object reference${byType.objects !== 1 ? 's' : ''}`);
  }
  if (byType.templates > 0) {
    parts.push(`${byType.templates} broken template reference${byType.templates !== 1 ? 's' : ''}`);
  }
  if (byType.tags > 0) {
    parts.push(`${byType.tags} broken tag reference${byType.tags !== 1 ? 's' : ''}`);
  }
  if (byType.folders > 0) {
    parts.push(`${byType.folders} broken folder reference${byType.folders !== 1 ? 's' : ''}`);
  }

  return `Found ${report.totalCount} issue${report.totalCount !== 1 ? 's' : ''}: ${parts.join(', ')}.`;
}

/**
 * Get detailed breakdown of broken references
 */
export function getDetailedBreakdown(report: SanityCheckReport): {
  category: string;
  count: number;
  items: BrokenReference[];
}[] {
  const breakdown: {
    category: string;
    count: number;
    items: BrokenReference[];
  }[] = [];

  const categories = [
    { key: 'types', label: 'Type References' },
    { key: 'strings', label: 'String References' },
    { key: 'objects', label: 'Object References' },
    { key: 'templates', label: 'Template References' },
    { key: 'tags', label: 'Tag References' },
    { key: 'folders', label: 'Folder References' },
  ] as const;

  for (const { key, label } of categories) {
    const items = report.brokenReferences.filter(ref => ref.referenceType === key);
    if (items.length > 0) {
      breakdown.push({
        category: label,
        count: items.length,
        items,
      });
    }
  }

  return breakdown;
}

/**
 * Store for sanity check reports (in-memory for now)
 */
class SanityCheckStore {
  private reports: Map<string, SanityCheckReport> = new Map();

  setReport(gameId: string, report: SanityCheckReport) {
    this.reports.set(gameId, report);
  }

  getReport(gameId: string): SanityCheckReport | null {
    return this.reports.get(gameId) || null;
  }

  clearReport(gameId: string) {
    this.reports.delete(gameId);
  }

  hasReport(gameId: string): boolean {
    return this.reports.has(gameId);
  }
}

export const sanityCheckStore = new SanityCheckStore();
